import { disableBeforeOpenDay, isBeforeOpenDay, eventOpenDay } from '../../utils/openDayDate';
/** @jsxImportSource @emotion/react */
import React, { useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { commonStyles } from '../../styles/commonStyles';
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Descriptions,
  Empty,
  Form,
  Input,
  InputNumber,
  List,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { HistoryOutlined } from '@ant-design/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import dayjs from 'dayjs';
import MainLayout from '../../components/common/MainLayout';
import { useGetBedByIdQuery, useUpdateBedMutation, useGetBedRotationHistoryQuery, useGetBedPropertiesQuery } from '../../api/fieldsApi';
import type { BedProperty } from '../../api/fieldsApi';
import {
  useGetPlantingsQuery,
  useCreatePlantingMutation,
  useUpdatePlantingMutation,
} from '../../api/plantingsApi';
import {
  useLazyGetBedEventsQuery,
  useCreateEventFromTemplateMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
} from '../../api/plantingEventsApi';
import type { PlantingEvent } from '../../types/plantingEvents';
import { useGetSaveRuntimeSessionQuery } from '../../api/simulationApi';
import { useGetEventTemplatesQuery } from '../../api/eventTemplatesApi';
import type { Bed } from '../../models/Bed';
import type { Planting, PlantingPayload, PlantingUpdatePayload, PlantingLifecycleStatus } from '../../models/Planting';
import PlantingFormModal from '../../features/plantings/components/PlantingFormModal';
import PlantingsList from '../../components/plantings/PlantingsList';
import { useRefreshWorld, useSave, useWorld } from '../../contexts/WorldContext';
import RotationHistoryBar from '../../components/rotation/RotationHistoryBar';
import BedEnvironmentCard from './components/BedEnvironmentCard';
import ScenarioDayControl from '../../components/scenarios/ScenarioDayControl';
import { toastError, toastSuccess } from '../../utils/toast';
import { hasAction } from '../../types/permissions';
import {
  useCreateSoilMeasurementByBedMutation,
  useGetEffectiveSoilStateByBedQuery,
  useGetSoilMeasurementsByBedQuery,
  useGetSoilStateByBedQuery,
  useUpsertSoilStateByBedMutation,
} from '../../api/soilStateApi';
import type { SoilMetrics } from '../../types/soil';
import { selectIsAuthenticated } from '../../store/slices/authSlice';
import { buildScenarioBed, buildScenarioPlanting, buildScenarioWorldIndexes, withPlantingRuntime } from '../../utils/scenarioWorldAdapters';
import { useTranslation } from 'react-i18next';
import i18n, { datePickerFormat, formatDate, formatDateTime } from '../../i18n';

const { Text } = Typography;

/** Soil metrics of the legacy soil state form; labels: `beds:soilMetrics.<key>`. */
const SOIL_METRIC_LABELS: Array<{ key: keyof SoilMetrics; suffix?: string }> = [
  { key: 'moisturePct', suffix: '%' },
  { key: 'temperatureC', suffix: '°C' },
  { key: 'pH' },
  { key: 'nitrogen' },
  { key: 'phosphorus' },
  { key: 'potassium' },
  { key: 'organicMatter', suffix: '%' },
  { key: 'compaction', suffix: '%' },
  { key: 'salinity' },
];

/** Soil data source codes; labels: `beds:soilSources.<code>`. */
const SOIL_SOURCE_CODES = ['default', 'estimated', 'measured', 'derived', 'manual', 'lab'] as const;

const soilSourceLabel = (code: string): string => i18n.t(`beds:soilSources.${code}`, { defaultValue: code });

const formatSoilMetric = (value?: number | null, suffix?: string) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${value}${suffix ?? ''}`;
};

/** Event kinds with a fallback name in `beds:eventKinds.<kind>`. */
const EVENT_NAME_KINDS = new Set([
  'transplant', 'relocation', 'created', 'status_changed', 'watering', 'fertilizing',
  'state_correction', 'pruning', 'health_check', 'pest_inspection', 'disease_inspection',
  'measurement', 'service',
]);

const getEventDisplayName = (event: any): string => {
  if (event.templateName) return event.templateName;
  if (event.kind && EVENT_NAME_KINDS.has(event.kind)) return i18n.t(`beds:eventKinds.${event.kind}`);
  return event.type || i18n.t('beds:eventKinds.fallback');
};

const resolveEventDate = (event: PlantingEvent): string | null => {
  return event.actualDate || event.plannedDate || null;
};

const toDateOnly = (value?: string | null): string | null => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
  return match ? match[1] : null;
};

const toDayjsDateOnly = (value?: string | null): dayjs.Dayjs | null => {
  const date = toDateOnly(value);
  if (!date) return null;
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed : null;
};

const formatDateOnly = (value?: string | null): string => {
  const date = toDateOnly(value);
  return date ? formatDate(date, 'short') : String(value || '—');
};

const resolveEditableEventDateField = (event: PlantingEvent): 'actualDate' | 'plannedDate' => {
  return event.status === 'completed' ? 'actualDate' : 'plannedDate';
};

const resolveEditableEventDateLabel = (event?: PlantingEvent | null): string => {
  if (!event) return i18n.t('beds:eventEdit.eventDate');
  return event.status === 'completed' ? i18n.t('beds:eventEdit.completedDate') : i18n.t('beds:eventEdit.plannedDate');
};

const ScenarioBedPage: React.FC = () => {
  const { t } = useTranslation('beds');
  const navigate = useNavigate();
  const { saveId, bedId } = useParams<{
    saveId: string;
    bedId: string;
  }>();
  const world = useWorld();
  const refreshWorld = useRefreshWorld();

  const indexes = useMemo(() => buildScenarioWorldIndexes(world.structure), [world.structure]);
  const rawBed = world.structure.beds.find((item) => String(item.id) === String(bedId));
  const runtimeBed = bedId ? world.runtime?.beds?.[String(bedId)] : null;

  const plantings = useMemo(() => {
    if (!rawBed) return [];
    return world.structure.plantings
      .filter((planting) => String(planting.bedId || '') === String(rawBed.id))
      .map((planting) => buildScenarioPlanting(
        withPlantingRuntime(planting, world.runtime?.plantings?.[String(planting.id)]),
        indexes,
      ));
  }, [indexes, rawBed, world.runtime?.plantings, world.structure.plantings]);

  if (!rawBed) {
    return (
      <MainLayout>
        <Card>{t('loadFailed')}</Card>
      </MainLayout>
    );
  }

  const bed = buildScenarioBed(rawBed, indexes);
  const plotName = bed.plotSummary?.name ?? bed.plotSummary?.id ?? '—';
  const fieldName = bed.fieldSummary?.name ?? bed.fieldSummary?.id ?? '—';

  return (
    <MainLayout>
      <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
        <ScenarioDayControl saveId={saveId} />

        <Alert
          type="info"
          showIcon
          message={t('scenario.title')}
          description={t('scenario.readOnlyHint')}
        />

        <Card
          title={bed.name}
          extra={<Button onClick={() => { void refreshWorld(); }}>{t('common:actions.refresh')}</Button>}
        >
          <Space direction="vertical" size={6}>
            <Text type="secondary">{t('location', { plot: plotName, field: fieldName })}</Text>
            <Descriptions size="small" column={1} bordered>
              <Descriptions.Item label={t('area')}>{bed.area ?? '—'}</Descriptions.Item>
              <Descriptions.Item label={t('moisture')}>
                {runtimeBed?.moisturePct != null ? `${runtimeBed.moisturePct}%` : '—'}
              </Descriptions.Item>
            </Descriptions>
          </Space>
        </Card>

        {bedId && saveId && <BedEnvironmentCard bedId={bedId} saveId={saveId} />}

        <Card title={t('scenario.plantings', { count: plantings.length })}>
          {plantings.length === 0 ? (
            <Empty description={t('scenario.noPlantings')} />
          ) : (
            <PlantingsList
              plantings={plantings}
              groupByCrop={false}
              onView={(planting) => navigate(`/saves/${saveId}/plantings/${planting.uuid ?? planting.id}`)}
            />
          )}
        </Card>

        <Card title={t('scenario.appliedEvents')}>
          {runtimeBed?.appliedEvents && runtimeBed.appliedEvents.length > 0 ? (
            <List
              dataSource={runtimeBed.appliedEvents}
              renderItem={(event) => (
                <List.Item>
                  <Space direction="vertical" size={0}>
                    <Text strong>{event.action || t('eventKinds.fallback')}</Text>
                    <Text type="secondary">{event.date || t('scenario.noDate')}</Text>
                  </Space>
                </List.Item>
              )}
            />
          ) : (
            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('scenario.noAppliedEvents')} />
          )}
        </Card>
      </Space>
    </MainLayout>
  );
};

const FarmBoundBedPage: React.FC = () => {
  const { t } = useTranslation('beds');
  const navigate = useNavigate();
  const { saveId, bedId } = useParams<{
    saveId: string;
    bedId: string;
  }>();
  const save = useSave();

  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector((state: any) => state.auth.user);

  const {
    data: bedData,
    isLoading: bedLoading,
    isError: bedError,
    refetch: refetchBed,
  } = useGetBedByIdQuery(
    { bedId: bedId ?? '' },
    { skip: !bedId, refetchOnMountOrArgChange: true }
  );

  const bed: Bed | undefined = bedData;

  const farmSummary = bed?.farmSummary;
  const fieldSummary = bed?.fieldSummary;
  const plotSummary = bed?.plotSummary;

  const resolvedFarmId = save.farmId ?? farmSummary?.id;
  const resolvedFieldId = fieldSummary?.id;
  const resolvedPlotId = plotSummary?.id;

  const { data: activeSimSession } = useGetSaveRuntimeSessionQuery(
    save.id,
    { skip: !save.id || !isAuthenticated }
  );
  const simSessionId = activeSimSession?.id ?? null;

  const {
    data: rotationHistory,
  } = useGetBedRotationHistoryQuery(
    bedId ? { bedId, simSessionId } : skipToken as any,
  );

  const {
    data: bedProperties = [],
    isLoading: bedPropertiesLoading,
  } = useGetBedPropertiesQuery(
    bedId ? { bedId, simSessionId } : skipToken as any,
    { skip: !bedId }
  );

  const farmName = farmSummary?.name ?? save.farmName ?? t('fallbackNames.save', { id: save.id.slice(0, 8) });
  const fieldName = fieldSummary?.name ?? t('fallbackNames.field', { id: resolvedFieldId });
  const plotName = plotSummary?.name ?? t('fallbackNames.plot', { id: resolvedPlotId });
  const bedName = bed?.name ?? `BED-${bedId}`;

  const [includeHistoricalPlantings, setIncludeHistoricalPlantings] = useState(false);

  const plantingsQueryArg = useMemo(() => {
    if (!bedId) return skipToken;
    const query: {
      saveId: string;
      bedId: string;
      limit: number;
      offset: number;
      lifecycleStatus?: PlantingLifecycleStatus;
      simSessionId?: string;
    } = {
      saveId: saveId || '',
      bedId,
      limit: includeHistoricalPlantings ? 200 : 100,
      offset: 0,
    };
    if (!includeHistoricalPlantings) {
      query.lifecycleStatus = 'active';
      if (simSessionId) {
        query.simSessionId = simSessionId;
      }
    }
    return query;
  }, [bedId, includeHistoricalPlantings, saveId, simSessionId]);

  const {
    data: plantings = [],
    isLoading: plantingsLoading,
    isError: plantingsError,
    refetch: refetchPlantings,
  } = useGetPlantingsQuery(plantingsQueryArg, {
    skip: !bedId,
  });

  const [createPlanting, { isLoading: creatingPlanting }] = useCreatePlantingMutation();
  const [updatePlanting, { isLoading: updatingPlanting }] = useUpdatePlantingMutation();
  const [updateBed] = useUpdateBedMutation();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingPlanting, setEditingPlanting] = useState<Planting | null>(null);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [bedSettingsOpen, setBedSettingsOpen] = useState(false);
  const [bedForm] = Form.useForm();
  const [soilStateOpen, setSoilStateOpen] = useState(false);
  const [soilMeasurementOpen, setSoilMeasurementOpen] = useState(false);
  const [soilStateForm] = Form.useForm();
  const [soilMeasurementForm] = Form.useForm();

  const [fetchBedEvents, { data: bedEvents = [], isLoading: loadingBedEvents, isFetching: fetchingBedEvents }] =
    useLazyGetBedEventsQuery();
  const [updateEvent] = useUpdateEventMutation();
  const [deleteEvent] = useDeleteEventMutation();

  const [editingEvent, setEditingEvent] = useState<PlantingEvent | null>(null);
  const [editEventOpen, setEditEventOpen] = useState(false);
  const [savingEvent, setSavingEvent] = useState(false);
  const [editEventForm] = Form.useForm();

  const {
    data: soilState,
    isLoading: soilStateLoading,
  } = useGetSoilStateByBedQuery(bedId ?? skipToken as any, {
    skip: !bedId,
  });

  const {
    data: effectiveSoilState,
    isLoading: effectiveSoilStateLoading,
  } = useGetEffectiveSoilStateByBedQuery(
    bedId ? { bedId } : skipToken as any,
    { skip: !bedId }
  );

  const {
    data: soilMeasurements = [],
    isLoading: soilMeasurementsLoading,
  } = useGetSoilMeasurementsByBedQuery(
    bedId ? { bedId, limit: 10 } : skipToken as any,
    { skip: !bedId }
  );

  const [upsertSoilState, { isLoading: savingSoilState }] = useUpsertSoilStateByBedMutation();
  const [createSoilMeasurement, { isLoading: savingSoilMeasurement }] = useCreateSoilMeasurementByBedMutation();
  const [createEventFromTemplate] = useCreateEventFromTemplateMutation();

  // Load event templates (save-scoped, Wave 5.1).
  const { data: eventTemplatesData } = useGetEventTemplatesQuery(
    { saveId: save?.id || '' },
    { skip: !save?.id }
  );
  const soilMeasurementTemplate = useMemo(() => {
    if (!eventTemplatesData?.templates) return null;
    return eventTemplatesData.templates.find((t: any) => t.code === 'soil_measurement') ?? null;
  }, [eventTemplatesData]);

  // Permission checks
  const canCreatePlantings = hasAction(user, 'plantings', 'plantings.create', String(resolvedFarmId));
  const canUpdatePlantings = hasAction(user, 'plantings', 'plantings.update', String(resolvedFarmId));
  const canUpdateBed = hasAction(user, 'beds', 'beds.update', String(resolvedFarmId));

  const openSoilStateModal = () => {
    soilStateForm.setFieldsValue({
      sourceType: soilState?.sourceType ?? 'manual',
      observedAt: soilState?.observedAt ?? undefined,
      confidencePct: soilState?.confidencePct ?? undefined,
      notes: soilState?.notes ?? undefined,
      ...soilState?.metrics,
    });
    setSoilStateOpen(true);
  };

  const openSoilMeasurementModal = () => {
    soilMeasurementForm.resetFields();
    soilMeasurementForm.setFieldsValue({
      sourceType: 'measured',
    });
    setSoilMeasurementOpen(true);
  };

  const defaultPlantingLocations = useMemo(() => {
    if (!bed) return undefined;
    
    // Используем resolved ID чтобы гарантировать наличие значений
    const effectivePlotId = resolvedPlotId ?? bed.plotId;
    const effectiveFieldId = resolvedFieldId;
    const effectiveFarmId = resolvedFarmId;
    
    return [
      {
        id: undefined,
        bedId: bed.id,
        bed: {
          id: bed.id,
          plot_id: effectivePlotId,
          row_number: bed.row_number ?? null,
          position_number: bed.position_number ?? null,
        },
        plot: effectivePlotId
          ? {
              id: effectivePlotId,
              name: plotSummary?.name ?? plotName,
              field_id: effectiveFieldId,
            }
          : undefined,
        field: effectiveFieldId
          ? {
              id: effectiveFieldId,
              name: fieldSummary?.name ?? fieldName,
              farm_id: effectiveFarmId,
            }
          : undefined,
        farm: effectiveFarmId
          ? {
              id: effectiveFarmId,
              name: farmSummary?.name ?? farmName,
            }
          : undefined,
      },
    ];
  }, [bed, resolvedPlotId, resolvedFieldId, resolvedFarmId, plotSummary, fieldSummary, farmSummary, plotName, fieldName, farmName]);

  const handleOpenCreateModal = () => {
    setEditingPlanting(null);
    setCreateModalOpen(true);
  };

  const handleOpenEditModal = (planting: Planting) => {
    setEditingPlanting(planting);
    setEditModalOpen(true);
  };

  const handleCreatePlantingSubmit = async (payload: PlantingPayload | PlantingUpdatePayload) => {
    try {
      if (!payload.locations?.length) {
        throw new Error(t('errors.noPlantingLocations'));
      }
      await createPlanting({ ...(payload as PlantingPayload), saveId: saveId || '' }).unwrap();
      toastSuccess(t('toasts.plantingCreated'));
      setCreateModalOpen(false);
      refetchPlantings();
    } catch (error) {
      toastError(error);
    }
  };

  const handleUpdatePlantingSubmit = async (payload: PlantingUpdatePayload) => {
    if (!editingPlanting) return;
    try {
      await updatePlanting({ saveId: saveId || '', id: editingPlanting.id, payload }).unwrap();
      toastSuccess(t('toasts.plantingUpdated'));
      setEditModalOpen(false);
      setEditingPlanting(null);
      refetchPlantings();
    } catch (error) {
      toastError(error);
    }
  };

  const handleOpenBedSettings = () => {
    if (!bed) return;
    bedForm.setFieldsValue({
      name: bed.name,
    });
    setBedSettingsOpen(true);
  };

  const handleSaveBedSettings = async () => {
    if (!bed || !bedId) return;
    try {
      const values = await bedForm.validateFields();
      await updateBed({ id: bedId, payload: values }).unwrap();
      toastSuccess(t('toasts.bedUpdated'));
      setBedSettingsOpen(false);
      refetchBed();
    } catch (error) {
      toastError(error);
    }
  };

  const handleSaveSoilState = async () => {
    if (!bedId) return;
    try {
      const values = await soilStateForm.validateFields();
      const metrics = SOIL_METRIC_LABELS.reduce<Record<string, number | null>>((acc, item) => {
        acc[item.key] = values[item.key] ?? null;
        return acc;
      }, {});
      await upsertSoilState({
        bedId,
        payload: {
          sourceType: values.sourceType,
          observedAt: values.observedAt ?? null,
          confidencePct: values.confidencePct ?? null,
          notes: values.notes ?? null,
          metrics,
        },
      }).unwrap();
      toastSuccess(t('toasts.soilStateSaved'));
      setSoilStateOpen(false);
    } catch (error) {
      toastError(error);
    }
  };

  const handleCreateSoilMeasurement = async () => {
    if (!bedId) return;
    try {
      const values = await soilMeasurementForm.validateFields();

      // If soil_measurement template is available, create event (new event model)
      if (soilMeasurementTemplate && resolvedFarmId) {
        // Map form values to property IDs from template
        const properties: Record<string, number> = {};
        const templateProps = soilMeasurementTemplate.properties || [];
        const SOIL_CODE_TO_FORM_KEY: Record<string, keyof SoilMetrics> = {
          soil_ph: 'pH',
          soil_moisture: 'moisturePct',
          soil_nitrogen: 'nitrogen',
          soil_phosphorus: 'phosphorus',
          soil_potassium: 'potassium',
          soil_organic_matter: 'organicMatter',
          soil_compaction: 'compaction',
          soil_salinity: 'salinity',
          soil_temp: 'temperatureC',
        };
        for (const tp of templateProps) {
          const code = tp.code;
          const formKey = SOIL_CODE_TO_FORM_KEY[code];
          if (formKey && values[formKey] !== undefined && values[formKey] !== null) {
            properties[String(tp.propertyId)] = values[formKey];
          }
        }
        await createEventFromTemplate({
          saveId: save.id,
          payload: {
            templateId: soilMeasurementTemplate.id,
            targets: [{ bedId }],
            properties,
            notes: values.notes ?? undefined,
          },
        }).unwrap();
        toastSuccess(t('toasts.soilMeasurementEvent'));
        setSoilMeasurementOpen(false);
        soilMeasurementForm.resetFields();
        return;
      }

      // Fallback: legacy soil_state_measurements
      const metrics = SOIL_METRIC_LABELS.reduce<Record<string, number | null>>((acc, item) => {
        acc[item.key] = values[item.key] ?? null;
        return acc;
      }, {});
      await createSoilMeasurement({
        bedId,
        payload: {
          sourceType: values.sourceType,
          measuredAt: values.measuredAt,
          sampleDepthFromCm: values.sampleDepthFromCm ?? null,
          sampleDepthToCm: values.sampleDepthToCm ?? null,
          confidencePct: values.confidencePct ?? null,
          notes: values.notes ?? null,
          metrics,
        },
      }).unwrap();
      toastSuccess(t('toasts.soilMeasurementAdded'));
      setSoilMeasurementOpen(false);
      soilMeasurementForm.resetFields();
    } catch (error) {
      toastError(error);
    }
  };

  const handleLoadBedHistory = async () => {
    if (!bedId) return;
    await fetchBedEvents({ bedId }).unwrap();
    setHistoryLoaded(true);
  };

  // Открытый день D+1: события датой раньше лежат в посчитанном прошлом —
  // дату и удаление сервер не принимает (EVENT_PAST_LOCKED), заметку — принимает.
  const openDay = eventOpenDay(save.cursor?.simDate || save.cursor?.sim_date || activeSimSession?.simDate);
  const isEventLocked = (event: PlantingEvent) => isBeforeOpenDay(resolveEventDate(event), openDay);
  const editingEventLocked = editingEvent ? isEventLocked(editingEvent) : false;

  const handleOpenEditEvent = (event: PlantingEvent) => {
    const initialDateRaw = resolveEventDate(event);
    const fallbackDate = openDay;
    const initialDate = toDayjsDateOnly(initialDateRaw) || toDayjsDateOnly(fallbackDate);

    setEditingEvent(event);
    setEditEventOpen(true);
    editEventForm.setFieldsValue({
      eventDate: initialDate && initialDate.isValid() ? initialDate : null,
      notes: event.notes || '',
    });
  };

  const handleSaveEventCorrection = async () => {
    if (!editingEvent) return;

    try {
      const values = await editEventForm.validateFields();
      const eventDate = values.eventDate?.format('YYYY-MM-DD');
      const dateField = resolveEditableEventDateField(editingEvent);
      setSavingEvent(true);

      await updateEvent({
        saveId: save.id,
        eventId: editingEvent.id,
        payload: isEventLocked(editingEvent)
          ? { notes: values.notes?.trim() || '' }
          : { notes: values.notes?.trim() || '', [dateField]: eventDate },
      }).unwrap();

      if (bedId) {
        await fetchBedEvents({ bedId }).unwrap();
      }
      setEditEventOpen(false);
      setEditingEvent(null);
      toastSuccess(t('toasts.eventCorrected'));
    } catch (err) {
      toastError(err);
    } finally {
      setSavingEvent(false);
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      await deleteEvent({ saveId: save.id, eventId }).unwrap();
      if (bedId) {
        await fetchBedEvents({ bedId }).unwrap();
      }
      toastSuccess(t('toasts.eventDeleted'));
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <MainLayout>
      {bedLoading ? (
        <Spin />
      ) : bedError || !bed ? (
        <Card>{t('loadFailed')}</Card>
      ) : (
        <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
          <Card
            title={bedName}
            extra={
              canUpdateBed && (
                <Button
                  type="text"
                  icon={<SettingOutlined />}
                  onClick={handleOpenBedSettings}
                  size="small"
                >
                  {t('settings.button')}
                </Button>
              )
            }
          >
            <Space direction="vertical" size={6}>
              <Text type="secondary">
                {t('location', { plot: plotName, field: fieldName })}
              </Text>
              <Space direction="vertical" size={2}>
                {bed.length !== undefined && bed.length !== null && (
                  <Text>{t('details.length', { value: bed.length })}</Text>
                )}
                {bed.area !== undefined && bed.area !== null && <Text>{t('details.area', { value: bed.area })}</Text>}
                {bed.row_number !== undefined && bed.row_number !== null && (
                  <Text>{t('details.row', { value: bed.row_number })}</Text>
                )}
                {bed.position_number !== undefined && bed.position_number !== null && (
                  <Text>{t('details.position', { value: bed.position_number })}</Text>
                )}
                {bed.geo_location && <Text>{t('details.geoLocation', { value: bed.geo_location })}</Text>}
                {bed.description && <Text>{t('details.description', { value: bed.description })}</Text>}
              </Space>
            </Space>
          </Card>

          {rotationHistory && (
            <Card title={t('rotation')} size="small">
              <RotationHistoryBar
                entries={rotationHistory.entries}
                families={rotationHistory.families}
                simDate={rotationHistory.simDate}
              />
            </Card>
          )}

          <Card
            title={t('soil.title')}
            extra={
              canUpdateBed && (
                <Space>
                  <Button onClick={openSoilMeasurementModal}>{t('soil.addMeasurement')}</Button>
                  <Button type="primary" onClick={openSoilStateModal}>{t('soil.editState')}</Button>
                </Space>
              )
            }
          >
            <Space direction="vertical" size={16} css={commonStyles.fullWidth}>
              <Card type="inner" title={t('soil.properties')} size="small">
                {bedPropertiesLoading ? (
                  <Spin />
                ) : bedProperties.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('soil.noProperties')} />
                ) : (
                  <Descriptions size="small" column={2} bordered>
                    {bedProperties.map((prop: BedProperty) => (
                      <Descriptions.Item
                        key={prop.propertyId}
                        label={prop.propertyName}
                      >
                        {prop.valueNumber !== null
                          ? `${prop.valueNumber}${prop.unit ? ` ${prop.unit}` : ''}`
                          : prop.valueText !== null
                          ? prop.valueText
                          : prop.valueBoolean !== null
                          ? (prop.valueBoolean ? t('common:actions.yes') : t('common:actions.no'))
                          : '—'}
                        {prop.valueDate && (
                          <Text type="secondary" style={{ marginLeft: 8, fontSize: 12 }}>
                            {formatDate(prop.valueDate, 'short')}
                          </Text>
                        )}
                      </Descriptions.Item>
                    ))}
                  </Descriptions>
                )}
              </Card>

              <Card type="inner" title={t('soil.currentState')} size="small">
                {soilStateLoading || effectiveSoilStateLoading ? (
                  <Spin />
                ) : soilState ? (
                  <Space direction="vertical" size={12} css={commonStyles.fullWidth}>
                    <Space wrap>
                      <Tag color="blue">{t('soil.sourceTag', { source: soilSourceLabel(soilState.sourceType) })}</Tag>
                      {soilState.confidencePct !== null && soilState.confidencePct !== undefined && (
                        <Tag color="gold">{t('soil.confidenceTag', { pct: soilState.confidencePct })}</Tag>
                      )}
                      {effectiveSoilState?.resolvedAt && (
                        <Tag>{t('soil.effectiveOn', { date: formatDate(effectiveSoilState.resolvedAt, 'short') })}</Tag>
                      )}
                    </Space>
                    <Descriptions size="small" column={2} bordered>
                      {SOIL_METRIC_LABELS.map((item) => (
                        <Descriptions.Item key={item.key} label={t(`soilMetrics.${item.key}`)}>
                          {formatSoilMetric(soilState.metrics[item.key], item.suffix)}
                        </Descriptions.Item>
                      ))}
                    </Descriptions>
                    {soilState.notes && <Text type="secondary">{soilState.notes}</Text>}
                  </Space>
                ) : (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('soil.noState')} />
                )}
              </Card>


              <Card type="inner" title={t('soil.recentMeasurements')} size="small">
                {soilMeasurementsLoading ? (
                  <Spin />
                ) : soilMeasurements.length === 0 ? (
                  <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('soil.noMeasurements')} />
                ) : (
                  <List
                    dataSource={soilMeasurements}
                    renderItem={(measurement) => (
                      <List.Item>
                        <List.Item.Meta
                          title={`${soilSourceLabel(measurement.sourceType)} · ${formatDateTime(measurement.measuredAt)}`}
                          description={
                            <Space direction="vertical" size={2}>
                              <Text type="secondary">
                                {t('soil.depth', { from: formatSoilMetric(measurement.sampleDepthFromCm, ` ${t('common:units.cm')}`), to: formatSoilMetric(measurement.sampleDepthToCm, ` ${t('common:units.cm')}`) })}
                              </Text>
                              <Text type="secondary">
                                {t('soil.measurementSummary', { moisture: formatSoilMetric(measurement.metrics.moisturePct, '%'), ph: formatSoilMetric(measurement.metrics.pH), nitrogen: formatSoilMetric(measurement.metrics.nitrogen) })}
                              </Text>
                              {measurement.notes && <Text type="secondary">{measurement.notes}</Text>}
                            </Space>
                          }
                        />
                      </List.Item>
                    )}
                  />
                )}
              </Card>
            </Space>
          </Card>

          {resolvedFarmId && bedId && (
            <BedEnvironmentCard bedId={bedId} saveId={save.id} />
          )}

          <Card
            title={t('plantings.title')}
            extra={
              <Space>
                {canCreatePlantings && (
                  <Button type="primary" onClick={handleOpenCreateModal}>
                    {t('plantings.add')}
                  </Button>
                )}
                <Button onClick={() => navigate(`/saves/${saveId}/crops`)}>
                  {t('plantings.manageCrops')}
                </Button>
                <Space size={12} align="center">
                  <Button onClick={() => refetchPlantings()}>{t('common:actions.refresh')}</Button>
                  <Space size={4} align="center">
                    <Switch
                      size="small"
                      checked={includeHistoricalPlantings}
                      onChange={setIncludeHistoricalPlantings}
                    />
                    <Text type="secondary">
                      {includeHistoricalPlantings ? t('plantings.showingAll') : t('plantings.activeOnly')}
                    </Text>
                  </Space>
                </Space>
              </Space>
            }
          >
            {plantingsLoading ? (
              <Spin />
            ) : plantingsError ? (
              <Card type="inner">{t('plantings.loadFailed')}</Card>
            ) : (
              <PlantingsList
                plantings={plantings}
                loading={plantingsLoading}
                viewMode="gallery"
                farmId={resolvedFarmId}
                groupByCrop={false}
                showLocationCode={false}
                onEdit={canUpdatePlantings ? handleOpenEditModal : undefined}
              />
            )}
          </Card>

          <Card
            title={t('history.title')}
            extra={
              <Button
                icon={<HistoryOutlined />}
                loading={loadingBedEvents || fetchingBedEvents}
                onClick={handleLoadBedHistory}
              >
                {historyLoaded ? t('common:actions.refresh') : t('history.load')}
              </Button>
            }
          >
            {!historyLoaded && !loadingBedEvents && (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('history.loadHint')}
              />
            )}

            {(loadingBedEvents || fetchingBedEvents) && <Spin />}

            {historyLoaded && !loadingBedEvents && bedEvents.length === 0 && (
              <Text type="secondary">{t('history.empty')}</Text>
            )}

            {historyLoaded && !loadingBedEvents && bedEvents.length > 0 && (
              <Space direction="vertical" size={8} css={commonStyles.fullWidth}>
                {bedEvents.map((event) => {
                  const eventDate = event.actualDate || event.plannedDate || event.createdAt;
                  return (
                    <Card key={event.id} size="small">
                      <Space direction="vertical" size={2} css={commonStyles.fullWidth}>
                        <Space size={8} wrap>
                          <Text strong>{getEventDisplayName(event)}</Text>
                          {eventDate && (
                            <Text type="secondary">
                              {formatDateOnly(eventDate)}
                            </Text>
                          )}
                          <Space size={6}>
                            <Button size="small" onClick={() => handleOpenEditEvent(event)}>
                              {t('history.correct')}
                            </Button>
                            {isEventLocked(event) ? (
                              <Tooltip title={t('simulation:openDay.pastLocked')}>
                                <Button size="small" danger disabled>
                                  {t('common:actions.delete')}
                                </Button>
                              </Tooltip>
                            ) : (
                            <Popconfirm
                              title={t('history.deleteConfirm')}
                              description={t('history.deleteConfirmHint')}
                              okText={t('common:actions.delete')}
                              cancelText={t('common:actions.cancel')}
                              okButtonProps={{ danger: true }}
                              onConfirm={() => handleDeleteEvent(event.id)}
                            >
                              <Button size="small" danger>
                                {t('common:actions.delete')}
                              </Button>
                            </Popconfirm>
                            )}
                          </Space>
                        </Space>
                        {event.notes ? <Text type="secondary">{event.notes}</Text> : null}
                      </Space>
                    </Card>
                  );
                })}
              </Space>
            )}
          </Card>
        </Space>
      )}

      <Modal
        title={t('eventEdit.title')}
        open={editEventOpen}
        onOk={handleSaveEventCorrection}
        onCancel={() => { setEditEventOpen(false); setEditingEvent(null); }}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={savingEvent}
      >
        <Form form={editEventForm} layout="vertical">
          <Form.Item
            label={resolveEditableEventDateLabel(editingEvent)}
            name="eventDate"
            rules={[{ required: true, message: t('eventEdit.dateRequired') }]}
            extra={editingEventLocked ? t('simulation:openDay.pastLocked') : undefined}
          >
            <DatePicker
              format={datePickerFormat()}
              css={commonStyles.fullWidth}
              disabled={editingEventLocked}
              disabledDate={disableBeforeOpenDay(openDay)}
            />
          </Form.Item>
          <Form.Item label={t('eventEdit.comment')} name="notes">
            <Input.TextArea rows={4} placeholder={t('eventEdit.commentPlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('settings.title')}
        open={bedSettingsOpen}
        onOk={handleSaveBedSettings}
        onCancel={() => setBedSettingsOpen(false)}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
      >
        <Form form={bedForm} layout="vertical">
          <Form.Item
            label={t('settings.name')}
            name="name"
            rules={[{ required: true, message: t('settings.nameRequired') }]}
          >
            <Input placeholder={t('settings.namePlaceholder')} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('soilStateModal.title')}
        open={soilStateOpen}
        onOk={handleSaveSoilState}
        onCancel={() => setSoilStateOpen(false)}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={savingSoilState}
      >
        <Form form={soilStateForm} layout="vertical">
          <Form.Item label={t('soilForm.source')} name="sourceType" initialValue="manual">
            <Select
              options={SOIL_SOURCE_CODES
                .filter((value) => value !== 'lab')
                .map((value) => ({ value, label: soilSourceLabel(value) }))}
            />
          </Form.Item>
          <Form.Item label={t('soilForm.observedAt')} name="observedAt">
            <Input placeholder="2026-03-20T12:00:00Z" />
          </Form.Item>
          <Form.Item label={t('soilForm.confidence')} name="confidencePct">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          {SOIL_METRIC_LABELS.map((item) => (
            <Form.Item key={item.key} label={t(`soilMetrics.${item.key}`)} name={item.key}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          ))}
          <Form.Item label={t('soilForm.notes')} name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={t('soilMeasurementModal.title')}
        open={soilMeasurementOpen}
        onOk={handleCreateSoilMeasurement}
        onCancel={() => setSoilMeasurementOpen(false)}
        okText={t('common:actions.save')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={savingSoilMeasurement}
      >
        <Form form={soilMeasurementForm} layout="vertical">
          <Form.Item label={t('soilForm.source')} name="sourceType" initialValue="measured">
            <Select
              options={SOIL_SOURCE_CODES.map((value) => ({ value, label: soilSourceLabel(value) }))}
            />
          </Form.Item>
          <Form.Item label={t('soilForm.measuredAt')} name="measuredAt">
            <Input placeholder="2026-03-20T12:00:00Z" />
          </Form.Item>
          <Form.Item label={t('soilForm.depthFrom')} name="sampleDepthFromCm">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={t('soilForm.depthTo')} name="sampleDepthToCm">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label={t('soilForm.confidence')} name="confidencePct">
            <InputNumber min={0} max={100} style={{ width: '100%' }} />
          </Form.Item>
          {SOIL_METRIC_LABELS.map((item) => (
            <Form.Item key={item.key} label={t(`soilMetrics.${item.key}`)} name={item.key}>
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          ))}
          <Form.Item label={t('soilForm.notes')} name="notes">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>


      <PlantingFormModal
        open={createModalOpen}
        title={t('plantingModal.createTitle')}
        confirmText={t('common:actions.create')}
        loading={creatingPlanting}
        farmId={resolvedFarmId}
        defaultLocations={defaultPlantingLocations}
        onCancel={() => setCreateModalOpen(false)}
        onSubmit={handleCreatePlantingSubmit}
      />

      <PlantingFormModal
        open={editModalOpen}
        title={t('plantingModal.editTitle')}
        confirmText={t('common:actions.save')}
        loading={updatingPlanting}
        farmId={resolvedFarmId}
        initialPlanting={editingPlanting}
        defaultLocations={defaultPlantingLocations}
        submitLocations={false}
        onCancel={() => {
          setEditModalOpen(false);
          setEditingPlanting(null);
        }}
        onSubmit={handleUpdatePlantingSubmit}
      />
    </MainLayout>
  );
};

const BedPage: React.FC = () => {
  const save = useSave();
  if (save.type === 'scenario') {
    return <ScenarioBedPage />;
  }
  return <FarmBoundBedPage />;
};

export default BedPage;
